/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function DefaultFooter({ content }) {
    const { brand, socials, menus, copyright, membruPic } = content;

    return (
        <MKBox component="footer" sx={{ minHeight: "100px", padding: "16px 0" }}>
            <Container>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={3} sx={{ mb: 3 }}>
                        <MKBox>
                            <Link to={brand.route}>
                                <MKBox
                                    component="img"
                                    src={brand.image}
                                    alt={brand.name}
                                    mb={2}
                                    sx={{
                                        width: { xs: "13rem", sm: "15rem" },
                                        maxWidth: "100%",
                                        height: "auto",
                                        objectFit: "contain",
                                    }}
                                    loading="lazy"
                                />
                            </Link>
                        </MKBox>
                        <MKBox display="flex" alignItems="center" mt={3}>
                            {socials.map(({ icon, link }, key) => (
                                <MKTypography
                                    key={link}
                                    component="a"
                                    href={link}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="h5"
                                    color="dark"
                                    opacity={0.8}
                                    mr={key === socials.length - 1 ? 0 : 2.5}
                                >
                                    {icon}
                                </MKTypography>
                            ))}
                        </MKBox>
                    </Grid>

                    <Grid item xs={12} md={3} sx={{ mb: 3 }}>
                        <MKTypography variant="h6" fontWeight="bold" mb={2}>
                            {brand.name}
                        </MKTypography>
                        {brand.address && (
                            <MKTypography variant="button" display="block" mt={1}>
                                <i
                                    className="fas fa-map-marker-alt"
                                    style={{ marginRight: "8px" }}
                                />
                                <MKTypography
                                    component="a"
                                    href={brand.address.mapLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ color: "inherit" }}
                                >
                                    <u>{brand.address.text}</u>
                                </MKTypography>
                            </MKTypography>
                        )}
                        {brand.phone && (
                            <MKTypography variant="button" display="block" mt={1}>
                                <i className="fas fa-phone" style={{ marginRight: "8px" }} />
                                <MKTypography
                                    component="a"
                                    href={`tel:${brand.phone.replace(/\s+/g, "")}`}
                                    sx={{ color: "inherit" }}
                                >
                                    <u>{brand.phone}</u>
                                </MKTypography>
                            </MKTypography>
                        )}
                        {brand.schedule &&
                            brand.schedule.map((line, idx) => (
                                <MKTypography
                                    key={idx}
                                    variant="button"
                                    display="block"
                                    mt={idx === 0 ? 1 : 0}
                                >
                                    {line}
                                </MKTypography>
                            ))}
                    </Grid>
                    {menus.map(({ name: title, items }) => (
                        <Grid key={title} item xs={6} sm={3} md={menus.length > 3 ? 1.5 : 2} sx={{ mb: 3 }}>
                            <MKTypography
                                display="block"
                                variant="button"
                                fontWeight="bold"
                                textTransform="capitalize"
                                mb={1}
                            >
                                {title}
                            </MKTypography>
                            <MKBox component="ul" p={0} m={0} sx={{ listStyle: "none" }}>
                                {items.map(({ name, route, href }) => (
                                    <MKBox key={name} component="li" p={0} m={0} lineHeight={1.25}>
                                        {href ? (
                                            <MKTypography
                                                component="a"
                                                href={href}
                                                target="_blank"
                                                rel="noreferrer"
                                                variant="button"
                                                fontWeight="regular"
                                                textTransform="capitalize"
                                            >
                                                {name}
                                            </MKTypography>
                                        ) : (
                                            <MKTypography
                                                component={Link}
                                                to={route}
                                                variant="button"
                                                fontWeight="regular"
                                                textTransform="capitalize"
                                            >
                                                {name}
                                            </MKTypography>
                                        )}
                                    </MKBox>
                                ))}
                            </MKBox>
                        </Grid>
                    ))}
                    <Grid item xs={12} sx={{ textAlign: "center", my: 3 }}>
                        {membruPic && (
                            <MKBox mb={2}>
                                <MKBox
                                    component="a"
                                    href={membruPic.link}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                >
                                    <MKBox
                                        component="img"
                                        src={membruPic.image}
                                        alt={membruPic.alt}
                                        sx={{ maxWidth: "220px", height: "auto" }}
                                        loading="lazy"
                                    />
                                </MKBox>
                            </MKBox>
                        )}
                        {copyright}
                    </Grid>
                </Grid>
            </Container>
        </MKBox>
    );
}

// Typechecking props for the DefaultFooter
DefaultFooter.propTypes = {
    content: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array]))
        .isRequired,
};

export default DefaultFooter;
